import { TooltipV2 } from "@/components/TooltipV2";
import { IconButtonV2, type IconButtonV2Size } from "@/components/interactive/ButtonV2";
import { cx } from "@/utils/classnames";
import { copyTextToClipboard } from "@/utils/clipboard-utils";
import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

/** How long the check icon stands in for the copy icon after a successful copy. */
const COPIED_STATE_DURATION_MS = 1_000;

export interface CopyButtonProps {
  /** Text placed on the clipboard, or an on-demand fetch performed only when clicked. */
  value: string | (() => Promise<string>);
  disabled?: boolean;
  /** Tooltip and accessible name at rest, phrased as an action: "Copy query ID". */
  tooltipText: string;
  /**
   * Accessible name while the copied state shows; the tooltip itself always reads "Copied".
   * Defaults to `tooltipText`, which holds the accessible name steady across the copy so a
   * screen reader does not rename the control mid-interaction. Pass a distinct announcement
   * ("Copied API key") where several copy buttons sit together and the confirmation needs to
   * say which one fired.
   */
  copiedAriaLabel?: string;
  size?: IconButtonV2Size;
  className?: string;
}

/**
 * Icon button that puts `value` on the clipboard and confirms in place by swapping to a check
 * for {@link COPIED_STATE_DURATION_MS}. The check is the whole confirmation — the button always
 * sits beside the value it copies, so a toast would only repeat what the icon already says.
 *
 * Copying goes through `copyTextToClipboard`, so it still works where `navigator.clipboard` is
 * missing (non-secure contexts, older browsers). The confirmation is gated on the write actually
 * landing, and a failure is the one case that needs words, since there is no check to show.
 */
export const CopyButton: React.FC<CopyButtonProps> = ({
  className,
  copiedAriaLabel,
  disabled,
  size = "3xs",
  tooltipText,
  value,
}) => {
  const [pending, setPending] = useState(false);
  const copyPendingRef = useRef(false);
  const [didRecentlyCopy, setDidRecentlyCopy] = useState(false);
  const resetCopyTimeoutRef = useRef<number | null>(null);
  // The clipboard write is async, so a copy can resolve after the button unmounts — a row
  // re-render or a closing modal is enough. Guard the state update rather than the timeout.
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (resetCopyTimeoutRef.current != null) {
        window.clearTimeout(resetCopyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (copyPendingRef.current) return;
    copyPendingRef.current = true;
    setPending(true);
    try {
      const text = typeof value === "function" ? await value() : value;
      if (!isMountedRef.current) return;
      const copied = await copyTextToClipboard(text);
      if (!isMountedRef.current) return;
      if (!copied) {
        toast.error("Failed to copy to clipboard");
        return;
      }
      setDidRecentlyCopy(true);
      if (resetCopyTimeoutRef.current != null) {
        window.clearTimeout(resetCopyTimeoutRef.current);
      }
      resetCopyTimeoutRef.current = window.setTimeout(
        () => setDidRecentlyCopy(false),
        COPIED_STATE_DURATION_MS,
      );
    } catch (error) {
      if (isMountedRef.current) {
        toast.error(error instanceof Error ? error.message : "Failed to copy to clipboard");
      }
    } finally {
      copyPendingRef.current = false;
      if (isMountedRef.current) setPending(false);
    }
  }, [value]);

  return (
    <TooltipV2
      content={didRecentlyCopy ? "Copied" : tooltipText}
      persistOnHover={false}
      placement={"bottom"}
    >
      <IconButtonV2
        aria-label={didRecentlyCopy ? copiedAriaLabel ?? tooltipText : tooltipText}
        className={cx("shrink-0", className)}
        icon={didRecentlyCopy ? "checkMark" : "copy"}
        size={size}
        type={"button"}
        variant={"tertiary"}
        disabled={disabled || pending}
        loading={pending}
        onClick={() => void handleCopy()}
      />
    </TooltipV2>
  );
};
